import React, { ForwardedRef, forwardRef, useContext, useEffect, useState } from 'react';
import { TextInput } from '@investoro/core';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import AdminPanelContext from 'utils/context/AdminPanelContex';
import { OrganizationRole } from 'utils/types/BrokerModels';

import Button from 'components/atom/Button';
import Indicator from 'components/atom/Indicator';
import Icon, { IconEnum } from 'components/common/Icon';
import InputContainer from 'components/common/InputContainer';
import Modal from 'components/common/Modal/Modal';
import TrashWithModal from 'components/common/TrashWithModal';
import { activeNotification } from 'components/functions/activeNotification';
import { Title } from 'components/mySettings/Title';

import AddAgentForm from './AddAgentForm';
import EditAgentForm from './EditAgentForm';

const AdminPanelContainer = styled.div`
    padding: 24px 15px 32px 15px;
    border-radius: var(--box-border-radius);
    background: var(--color-white);
    display: flex;
    flex-direction: column;
    gap: 30px;
    margin-bottom: 60px;
`;

const InfoWrapperContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const InfoWrapper = styled.span`
    position: relative;
    display: flex;
    gap: 10px;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    background: var(--color-alt-second);
    color: var(--color-primary);
    padding: 15px 19px;
    border-radius: var(--border-radius);
    height: 50px;

    @media (max-width: 480px) { 
        height: 70px;
        border-radius: var(--image-border-radius);
`;

const Label = styled.span`
    font-weight: var(--font-weight);
    font-size: var(--font-size-body-2);
    line-height: 20px;
    background: transparent;
    border: none;
    color: var(--color-primary);

    @media (max-width: 768px) {
        font-size: 12px;
`;

const ValueBox = styled.div`
    line-height: 30px;
    text-align: right;

    @media (max-width: 768px) {
        font-size: 12px;

    }
`;

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TableScrollContainer = styled.div`
    @media (max-width: 768px) {
        width: 100%;
        overflow-x: auto;
    }
`;

const SectionTitle = styled.div`
    margin: 10px 0 0 0;
    font-weight: var(--font-weight);
    font-size: 18px;
    line-height: 30px;
    color: var(--color-primary);
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.th`
    padding: 10px;
    text-align: left;
    font-weight: bold;
    font-size: 14px;
`;

const TableRow = styled.tr<{ isGray: boolean }>`
    background-color: ${(props) => props.isGray ? 'var(--color-alt-second)' : 'var(--color-white)'};
    td:first-child {
        border-top-left-radius: var(--image-border-radius);
        border-bottom-left-radius: var(--image-border-radius);
    }

    td:last-child {
        border-top-right-radius: var(--image-border-radius);
        border-bottom-right-radius: var(--image-border-radius);
    }
`;

const TableCell = styled.td<{ isActive?: boolean }>`
    padding: 15px;
    font-size: 14px;
    span {
        color: ${(props) => props.isActive ? 'var(--color-success)' : 'var(--color-error)'};
    }
`;

const ActionButtonWrapper = styled.div`
    display: flex;
    gap: 10px;
`;

interface ActionButtonProps {
    darkMode?: boolean;
}

const ActionButton = styled.div<ActionButtonProps>`
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;    

    ${(props) => props.darkMode && `
        img {
            filter: brightness(0) invert(1);
        }
    `}

    &:hover {
        background: var(--color-alt);
        cursor: pointer;

        img {
            filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%);
        }
    }
`;

const AddAgentButton = styled.div`
    cursor: pointer;
    color: var(--color-primary);
    font-weight: var(--font-weight);
    display: flex;
    align-items: center;
    font-size: 15px;
    height: 50px;
    border: 2px solid var(--color-primary);
    border-radius: 10px;
    padding: 12px;
    width: fit-content;
  
    @media (max-width: 768px) {
        height: auto;
        align-self: flex-start;
    }
`;

const ChangeSmsModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    align-items: center;
    padding: 40px;
`;

const ModalContainer = styled.div`
    display: flex;
    gap: 50px;
    justify-content: center;
    width: 90%;

    > span {
        width: 100%;
    }
    
    &:last-child {
        margin-top: 20px;

        button {
            min-width: 220px;
        }
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 15px;
    }
`;

interface AdminPanelSettingsProps {
    title: string;
}

const AdminSettings = forwardRef<HTMLDivElement, AdminPanelSettingsProps>(({ title }, ref: ForwardedRef<HTMLDivElement>) => {
    const { user } = useUser();
    const { adminPanelState, changeAgentActiveStatus, changeSmsLimitToAdmin, deleteAgent, loadAgentDetails } = useContext(AdminPanelContext);
    const [showChangeSmsPackageForm, setShowChangeSmsPackageForm] = useState(false);
    const [adminSmsPackage, setAdminSmsPackage] = useState(0);
    const [triggerValidation, setTriggerValidation] = useState(false);

    const [showAddAgentForm, setShowAddAgentForm] = useState(false);
    const [showEditAgentModal, setShowEditAgentModal] = useState(false);

    const handleAddAgent = () => {
        if (adminPanelState.totalAccountNumber === adminPanelState.existedAccountNumber) {
            activeNotification('Liczba agentów', 'Wykorzystałeś maksymalną liczbę agentów', 'warning');

            return;
        }
        setShowAddAgentForm(true);
    };

    const handleEditAgent = async (agentId: string) => {
        await loadAgentDetails(agentId);
        setShowEditAgentModal(true);
    };

    const validateSmsLimit = (smsLimit: number) => {
        if (smsLimit < 0) {
            return 'Liczba SMS nie może być ujemna';
        } else if (smsLimit > adminPanelState.totalSmsAvailableToNewAccounts) {
            return `Przekroczono dostępną pulę SMS (max: ${adminPanelState.totalSmsAvailableToNewAccounts})`;
        }

        return '';
    };

    const handleChangeSmsPackageSubmit = () => {
        setTriggerValidation(true);
        const validationError = validateSmsLimit(adminSmsPackage);

        if (!validationError) {
            changeSmsLimitToAdmin(adminSmsPackage);
            setShowChangeSmsPackageForm(false);
        }
    };

    useEffect(() => {
        if (adminPanelState?.agents && adminPanelState.agents.length > 0) {
            const adminAgent = adminPanelState.agents.find((agent) => agent.organizationRole === OrganizationRole.ORGANIZATION_ADMIN);

            if (adminAgent) {
                setAdminSmsPackage(adminAgent.smsPackage || 0);
            }
        }
    }, [adminPanelState.agents]);

    const agencyInfo = [
        {
            label: 'Liczba agentów:',
            value: adminPanelState.numberOfAgents
        },
        {
            label: 'Pakiet aktywny do:',
            value: adminPanelState.packageDate ? new Date(adminPanelState.packageDate).toLocaleDateString() : ''
        },
        {
            label: 'Liczba SMS:',
            value: adminPanelState.smsPackage
        }
    ];

    return (
        <div ref={ref}>
            <Title>{title}</Title>
            <AdminPanelContainer>
                <InfoWrapperContainer>
                    <SectionTitle>{adminPanelState.agencyName}</SectionTitle>
                    {agencyInfo.map((el) => <InfoWrapper key={el.label}>
                        <Label>{el.label}</Label>
                        <ValueBox>{el.value}</ValueBox>
                    </InfoWrapper>)}
                </InfoWrapperContainer>

                <AddAgentButton onClick={handleAddAgent}>+ Dodaj agenta</AddAgentButton>

                <TableWrapper>
                    <SectionTitle>Lista agentów</SectionTitle>
                    {adminPanelState.isLoading
                        ? <Indicator />
                        : <TableScrollContainer>
                            <Table>
                                <thead>
                                    <tr>
                                        <TableHeader></TableHeader>
                                        <TableHeader>Imię</TableHeader>
                                        <TableHeader>Nazwisko</TableHeader>
                                        <TableHeader>Email</TableHeader>
                                        <TableHeader>SMS</TableHeader>
                                        <TableHeader>Ostatnie logowanie</TableHeader>
                                        <TableHeader>Status</TableHeader>
                                    </tr>
                                </thead>
                                <tbody>
                                    {adminPanelState.agents && adminPanelState.agents.length > 0 && adminPanelState.agents.map((agent, index) => <TableRow key={agent.agentId} isGray={index % 2 === 0}>
                                        <TableCell>
                                            {agent.organizationRole === OrganizationRole.ORGANIZATION_AGENT
                                                ? <ActionButtonWrapper>
                                                    <ActionButton darkMode={user.isDarkMode} onClick={() => handleEditAgent(agent.agentId)}>
                                                        <Icon icon={IconEnum.EDIT}/>
                                                    </ActionButton>
                                                    <ActionButton darkMode={user.isDarkMode} onClick={() => {
                                                        changeAgentActiveStatus(agent.agentId, !agent.activationStatus);
                                                    }}>
                                                        <Icon icon={IconEnum.BLOCKED}/>
                                                    </ActionButton>
                                                    <ActionButton darkMode={user.isDarkMode}>
                                                        <TrashWithModal
                                                            iconText={''}
                                                            title={'Czy na pewno chcesz usunąć pracownika?'}
                                                            onChange={() => deleteAgent(agent.agentId)}
                                                            filter={`${agent.firstName} ${agent.lastName}`}/>
                                                    </ActionButton>
                                                </ActionButtonWrapper>
                                                : <ActionButtonWrapper>
                                                    <ActionButton darkMode={user.isDarkMode} onClick={() => setShowChangeSmsPackageForm(true)}>
                                                        <Icon icon={IconEnum.EDIT}/>
                                                    </ActionButton>
                                                </ActionButtonWrapper>
                                            }
                                        </TableCell>
                                        <TableCell>{agent.firstName}</TableCell>
                                        <TableCell>{agent.lastName}</TableCell>
                                        <TableCell>{agent.email}</TableCell>
                                        <TableCell>{agent.smsPackage}</TableCell>
                                        <TableCell>{agent.lastLogin ? new Date(agent.lastLogin).toLocaleString() : '-'}</TableCell>
                                        <TableCell isActive={agent.activationStatus}>
                                            {agent.organizationRole === OrganizationRole.ORGANIZATION_ADMIN
                                                ? <span>Administrator</span>
                                                : agent.activationStatus
                                                    ? <span>Aktywny</span>
                                                    : <span>Nieaktywny</span>
                                            }
                                        </TableCell>
                                    </TableRow>)}
                                </tbody>
                            </Table>
                        </TableScrollContainer>
                    }
                </TableWrapper>

                <Modal
                    isOpen={showAddAgentForm}
                    onClose={() => setShowAddAgentForm(false)}
                    centeredContent
                    backgroundColor={user.isDarkMode ? 'var(--color-alt-hover)' : '#fff'}
                >
                    <AddAgentForm
                        onClose={() => setShowAddAgentForm(false)}
                    />
                </Modal>

                <Modal
                    isOpen={showEditAgentModal}
                    onClose={() => setShowEditAgentModal(false)}
                    centeredContent
                    backgroundColor={user.isDarkMode ? 'var(--color-alt-hover)' : '#fff'}
                >
                    <EditAgentForm
                        onClose={() => setShowEditAgentModal(false)}
                    />
                </Modal>

                <Modal
                    isOpen={showChangeSmsPackageForm}
                    onClose={() => setShowChangeSmsPackageForm(false)}
                    centeredContent
                    backgroundColor={user.isDarkMode ? 'var(--color-alt-hover)' : '#fff'}
                    strictContentHeight
                >
                    <ChangeSmsModalContainer>
                        <h1>Limit SMS dla administratora</h1>
                        <ModalContainer>
                            <InputContainer title="">
                                <TextInput
                                    value={adminSmsPackage}
                                    onChange={(e) => setAdminSmsPackage(parseInt(e.target.value) || 0)}
                                    placeholder="Wprowadź liczbę SMS..."
                                    required
                                    errorMessage={validateSmsLimit(adminSmsPackage)}
                                    inputValidation={() => !!validateSmsLimit(adminSmsPackage)}
                                    triggerValidation={triggerValidation}
                                />
                            </InputContainer>
                        </ModalContainer>
                        <ModalContainer>
                            <Button onClick={handleChangeSmsPackageSubmit}>
                                Zapisz zmiany
                            </Button>
                        </ModalContainer>
                    </ChangeSmsModalContainer>
                </Modal>
            </AdminPanelContainer>
        </div>
    );
});

export default AdminSettings;
